<script lang="ts">
import { defineComponent, h, nextTick, onActivated, onBeforeUnmount, onDeactivated, onMounted, onUpdated, ref, Teleport } from 'vue'
import { Pagination } from 'ant-design-vue'

// 2026-10-10：数字分页统一底部居中，底栏预留真实空间，不覆盖正文、滚动条及弹窗按钮。
const docks = new WeakMap<HTMLElement, { element: HTMLElement; users: Set<HTMLElement> }>()
const toolbarUsers = new WeakMap<HTMLElement, Set<HTMLElement>>()
export default defineComponent({
  name: 'APagination',
  inheritAttrs: false,
  setup(_, { attrs, slots }) {
    const anchor = ref<HTMLElement>()
    const panel = ref<HTMLElement>()
    const outlet = ref<HTMLElement>()
    let frame = 0, active = true, disposed = false
    let mutations: MutationObserver | undefined
    let scope: HTMLElement | undefined
    let host: HTMLElement | undefined
    let toolbar: HTMLElement | undefined
    const visible = (el: HTMLElement) => el.isConnected && el.getClientRects().length > 0 && getComputedStyle(el).visibility !== 'hidden'
    function updateDock() {
      if (!outlet.value) return
      const hidden = !Array.from(outlet.value.children).some(el => el instanceof HTMLElement && el.style.display !== 'none')
      if (outlet.value.hidden !== hidden) outlet.value.hidden = hidden
    }
    function releaseDock() {
      if (!scope || !anchor.value) return
      const dock = docks.get(scope)
      dock?.users.delete(anchor.value)
      if (dock && !dock.users.size) { dock.element.remove(); docks.delete(scope) }
      scope = undefined
    }
    function releaseToolbar() {
      if (!toolbar || !anchor.value) return
      const users = toolbarUsers.get(toolbar)
      users?.delete(anchor.value)
      if (!users?.size) { toolbar.classList.remove('admin-pagination-toolbar'); toolbarUsers.delete(toolbar) }
      toolbar = undefined
    }
    function attachDock(target: HTMLElement, source: HTMLElement) {
      releaseDock()
      scope = target
      let dock = docks.get(target)
      if (!dock) {
        const element = document.createElement('div')
        element.className = 'admin-pagination-dock'
        element.setAttribute('aria-label', '列表分页')
        element.hidden = true
        // 正文滚动区的兄弟底栏；弹窗/抽屉中放在正文之后、确认按钮之前。
        const body = target.id === 'appmain' ? target : target.querySelector<HTMLElement>('.ant-modal-body, .ant-drawer-body, .c-modal-body')
        if (body?.parentElement) body.insertAdjacentElement('afterend', element)
        else target.append(element)
        dock = { element, users: new Set() }
        docks.set(target, dock)
      }
      dock.users.add(source)
      outlet.value = dock.element
    }
    function layout() {
      frame = 0
      const source = anchor.value
      if (!source) return
      const target = source.closest<HTMLElement>('.ant-modal-content, .ant-drawer-content, .c-modal-cover, #appmain')
      if (target && (scope !== target || !outlet.value?.isConnected)) attachDock(target, source)
      const content = panel.value
      if (!content) { if (target) nextTick(schedule); return }
      const drawer = source.closest('.ant-drawer')
      // 沿用弹窗隔离：上层弹窗打开后，下层页码不接收操作。
      const modalSelector = '.ant-modal-wrap, .ant-drawer-open > .ant-drawer-content-wrapper, .c-modal-cover'
      const modals = Array.from(document.querySelectorAll<HTMLElement>(modalSelector)).filter(visible)
      const topModal = modals.sort((a, b) => (Number(getComputedStyle(a).zIndex) || 1000) - (Number(getComputedStyle(b).zIndex) || 1000)).pop()
      const show = active && !!target && visible(source) && visible(target) && (!drawer || drawer.classList.contains('ant-drawer-open')) && (!topModal || topModal === source.closest(modalSelector))
      if (show && target) {
        const row = Array.from(target.querySelectorAll<HTMLElement>('.admin-list-toolbar, .query-command, .query, .guide-toolbar, [data-pagination-toolbar]'))
          .find(el => visible(el) && !el.closest('.ant-table') && el.closest('.ant-modal-content, .ant-drawer-content, .c-modal-cover, #appmain') === target)
        if (toolbar !== row) { releaseToolbar(); toolbar = row }
        if (toolbar) {
          const users = toolbarUsers.get(toolbar) || new Set<HTMLElement>()
          users.add(source)
          toolbarUsers.set(toolbar, users)
          if (!toolbar.classList.contains('admin-pagination-toolbar')) toolbar.classList.add('admin-pagination-toolbar')
        }
      }
      const display = show ? 'block' : 'none'
      if (content.style.display !== display) content.style.display = display
      updateDock()
    }
    function schedule() { if (!disposed && !frame) frame = requestAnimationFrame(layout) }
    function containWheel(event: WheelEvent) {
      const dropdown = (event.target as HTMLElement).closest('.ant-select-dropdown')
      if (!dropdown) return
      event.stopPropagation()
      const list = dropdown.querySelector<HTMLElement>('.rc-virtual-list-holder')
      if (!list || (event.deltaY < 0 && list.scrollTop <= 0) || (event.deltaY > 0 && list.scrollTop + list.clientHeight >= list.scrollHeight - 1)) event.preventDefault()
    }
    onMounted(async () => {
      const parent = anchor.value?.parentElement
      if (parent && parent.children.length === 1 && !parent.textContent?.trim()) {
        host = parent
        host.classList.add('admin-pagination-host')
      }
      layout()
      await nextTick()
      if (disposed) return
      panel.value?.addEventListener('wheel', containWheel, { passive: false })
      mutations = new MutationObserver(schedule)
      mutations.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'style', 'hidden'] })
      schedule()
    })
    onUpdated(schedule)
    onActivated(() => { active = true; schedule() })
    onDeactivated(() => { active = false; schedule() })
    onBeforeUnmount(() => {
      active = false
      disposed = true
      cancelAnimationFrame(frame)
      mutations?.disconnect()
      panel.value?.removeEventListener('wheel', containWheel)
      if (panel.value) panel.value.style.display = 'none'
      updateDock()
      releaseDock()
      releaseToolbar()
      host?.classList.remove('admin-pagination-host')
    })
    return () => h('div', { ref: anchor, class: 'admin-pagination-anchor' }, outlet.value ? [
      h(Teleport, { to: outlet.value }, h('div', { ref: panel, class: 'admin-pagination-panel', style: { display: 'none' } }, [h(Pagination, {
        showLessItems: true, ...attrs, getPopupContainer: () => panel.value || document.body
      }, slots)]))
    ] : [])
  }
})
</script>

<style lang="scss">
// 2026-10-10：底栏在内容区域居中，横向滚动正文和切换页码均不移动底栏。
.admin-pagination-anchor { width: 0; height: 0; min-width: 0; flex: 0 0 0; }
.admin-pagination-dock {
  display: flex;
  flex: 0 0 auto;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: 8px 20px;
  min-width: 0;
  max-width: 100%;
  padding: 8px 12px;
  box-sizing: border-box;
  border-top: 1px solid #f0f0f0;
  background: #fff;
  &[hidden] { display: none; }
}
.admin-pagination-panel {
  position: relative;
  max-width: 100%;
  min-width: 0;
  background: #fff;
  .ant-pagination { display: flex; flex-wrap: wrap; justify-content: center; align-items: center; gap: 4px 0; margin: 0; }
  .ant-pagination > li { flex: 0 0 auto; }
  .ant-pagination-options { display: flex; align-items: center; flex-wrap: nowrap; }
  .ant-pagination-options-quick-jumper { white-space: nowrap; }
  .ant-select-dropdown .rc-virtual-list-holder { overscroll-behavior: contain; }
}
#appmain .admin-pagination-host, .ant-modal .admin-pagination-host, .ant-drawer .admin-pagination-host, .c-modal-cover .admin-pagination-host {
  overflow: visible;
  width: 0 !important;
  height: 0 !important;
  min-height: 0;
  margin: 0 !important;
  padding: 0 !important;
}
.c-modal-container > .admin-pagination-dock { margin-bottom: 44px; }
.c-modal-container:has(> .admin-pagination-dock:not([hidden])) > .c-modal-body { margin-bottom: 0; }
</style>
